import React, {Component, ReactElement, ReactNode} from 'react'
import {
    INFOMATION_TYPE,
    MANAGEMENT_CATEGORY,
    MANSION_CATEGORY,
    ROLE_FOR_ADMINISTRATOR,
    VISITORS_RANGE,
} from '../../../CodeMaster';
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage';
import {Nullable} from '../../../app/TypeHelpers';
import {Messages} from '../../../Messages';
import {HKConstants} from '../../../HKConstants';
import AuthorityChecker from '../../../components/AuthorityChecker';
import ListSearchFormArea from '../../../styledComponents/adminPage/ListSearchFormArea';
import SearchPageActionArea from '../../../styledComponents/adminPage/SearchPageActionArea';
import {AdminDefaultMediumButton} from '../../../styledComponents/adminPage/Buttons';
import {PageMainArea} from '../../../components/shared/hoc/withAdminPageTemplate';
import {SortOrder} from '../../../components/shared/SortOrder';
import {SessionStorageHelper} from '../../../SessionStorageHelper';
import {COMMON_CONSTANTS} from '../../../CommonConstants';
import {SearchResultCountDisplayProps} from '../../../styledComponents/adminPage/SearchResultCountDisplay';
import SortableTableHeader, {
    SortableTableHeaderCell
} from '../../../styledComponents/adminPage/SortablePaginationTableHeader';
import SortablePaginationTable from '../../../styledComponents/adminPage/SortablePaginationTable';
import SortablePaginationTableBody from '../../../styledComponents/adminPage/SortablePaginationTableBody';
import {SortDirection} from '@mui/material/TableCell';
import {formatDateTimeForUpdateDate, formatForDateInput} from '../../../formatters/formatDate';
import {
    ConditionButtonCell,
    ConditionCategoryCell,
    ConditionColumNameCell,
    ConditionInputCell,
    ConditionTable,
    ConditionTableBody,
    ConditionTableRow,
} from '../../../styledComponents/adminPage/ConditionTable'
import PPSelect, {OptionProps} from '../../../components/shared/forms/PPSelect'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import PPDatePicker from '../../../components/shared/forms/PPDatePicker'
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {StyledDiv, StyledSpan} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import NatApartAssocInfoRepo from './repository/NatApartAssocInfoRepo'
import {
    ApartAssocInfo,
    NatGetApartAssocInfoList,
    SearchCondition,
    SearchConditionPageInfo,
    SearchResult,
} from './repository/NatGetApartAssocInfoList'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import NatCurrentDateTimeRepo from '../currentDateTime/repository/NatCurrentDateTimeRepo'
import {NatGetCurrentDateTime} from '../currentDateTime/repository/NatGetCurrentDateTime'
import {Location} from 'react-router-dom'

import {Link, TableCell, TableRow} from '@mui/material';
import {SelectChangeEvent} from '@mui/material/Select'

const KEY_LOGIN_USER_ROLE_ID: string = 'loginUserRoleId'
const KEY_SEARCH_CONDITION: string = 'adminApartAssocListPageSearchCondition'

interface Props {
    apartAssocInfoRepo: NatApartAssocInfoRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    searchResult: SearchResult
    searchCondition: SearchCondition
    searchConditionPageInfo: SearchConditionPageInfo
    searchConditionSortInfo: SortOrder[]
    selectedInfomationType: string
    extractStartDate: string
    extractEndDate: string
    extractStartMaxDate: string
    extractEndMinDate: string
    selectedCategory: string
    apartAssocList: ApartAssocInfo[]
    isLoadCompleted: boolean
}


class NatAdminApartAssocInfoListPage extends Component<Props & AdminPagePropsSet, States> {
    roleId: Nullable<string>
    subSortKey: SortOrder = new SortOrder('apartAssocInfoId', 'asc')

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        // 前回検索条件が保持されていればデフォルト値として設定する
        let defaultSearchCondition: SearchCondition = {}
        const sessionStorageSearchCondition: string | null = window.sessionStorage.getItem(KEY_SEARCH_CONDITION)
        if (sessionStorageSearchCondition) {
            defaultSearchCondition = JSON.parse(sessionStorageSearchCondition)
        }

        // 管理組合役員の場合、情報種別は固定
        this.roleId = SessionStorageHelper.getStringItemOrNull(KEY_LOGIN_USER_ROLE_ID)
        if (this.roleId === ROLE_FOR_ADMINISTRATOR.ASSOC.value) {
            defaultSearchCondition.infoCategoryId = INFOMATION_TYPE.ASSOC_INFO.value
        }

        this.state = {
            searchResult: {},
            searchCondition: defaultSearchCondition,
            searchConditionPageInfo: {
                ['pageNum']: COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0],
                ['page']: COMMON_CONSTANTS.FIRST_PAGE,
            },
            searchConditionSortInfo: [
                new SortOrder('sortOrder', 'asc'),
                new SortOrder('updateDatetime', 'desc'),
            ],
            selectedInfomationType: (defaultSearchCondition.infoCategoryId) ?
                defaultSearchCondition.infoCategoryId : '',
            extractStartDate: (defaultSearchCondition.publishStartDate) ?
                defaultSearchCondition.publishStartDate : '',
            extractEndDate: (defaultSearchCondition.publishEndDate) ?
                defaultSearchCondition.publishEndDate : '',
            extractStartMaxDate: (defaultSearchCondition.publishEndDate) ?
                defaultSearchCondition.publishEndDate : '',
            extractEndMinDate: (defaultSearchCondition.publishStartDate) ?
                defaultSearchCondition.publishStartDate : '',
            selectedCategory: (defaultSearchCondition.categoryId) ?
                defaultSearchCondition.categoryId : '',
            apartAssocList: [],
            isLoadCompleted: false,
        }

        this.handleChangePage = this.handleChangePage.bind(this)
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
        this.handleSortLifeNotes = this.handleSortLifeNotes.bind(this)
        this.onClickSearchButton = this.onClickSearchButton.bind(this)
        this.onChangeInfomationType = this.onChangeInfomationType.bind(this)
        this.onChangeExtractStartDate = this.onChangeExtractStartDate.bind(this)
        this.onChangeExtractEndDate = this.onChangeExtractEndDate.bind(this)
        this.onChangeCategory = this.onChangeCategory.bind(this)
        this.onClickRegisterApartAssocButton = this.onClickRegisterApartAssocButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const searchCondition = {...this.state.searchCondition}
            if (!searchCondition.publishStartDate && !searchCondition.publishEndDate) {
                // 初期状態の抽出範囲に現在日時を設定
                const currentDateTime: Nullable<NatGetCurrentDateTime> = await this.props.asyncCallFunction(
                    this.props.currentDateTimeRepo.getCurrentDateTime.bind(this.props.currentDateTimeRepo),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (currentDateTime?.details) {
                    const defaultSearchDate = formatForDateInput(currentDateTime.details.currentDateTime)
                    searchCondition.publishStartDate = defaultSearchDate
                    searchCondition.publishEndDate = defaultSearchDate
                    this.setState({
                        searchCondition: searchCondition,
                        extractStartDate: defaultSearchDate,
                        extractEndDate: defaultSearchDate,
                    })
                }
            }
            await this.searchApartAssocInfo(
                this.state.searchCondition, this.state.searchConditionPageInfo, this.state.searchConditionSortInfo,
            )
        } finally {
            this.setState({isLoadCompleted: true})
            this.props.eventEmitter.closeLoading()
        }
        this.showInitialMessage()
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    async searchApartAssocInfo(
        searchCondition?: SearchCondition,
        searchConditionPageInfo?: SearchConditionPageInfo,
        searchConditionSortInfos?: SortOrder[],
    ): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {

            // ソートのサブキーを追加
            let addSearchConditionSubSortInfo: SortOrder[] = []
            if (searchConditionSortInfos) {
                addSearchConditionSubSortInfo = searchConditionSortInfos.concat(this.subSortKey)
            } else {
                addSearchConditionSubSortInfo = addSearchConditionSubSortInfo.concat(this.subSortKey)
            }

            const response: Nullable<NatGetApartAssocInfoList> =
                await this.props.asyncCallFunction(
                    this.props.apartAssocInfoRepo.getApartAssocInfoList.bind(
                        this.props.apartAssocInfoRepo,
                        searchCondition, searchConditionPageInfo, addSearchConditionSubSortInfo,
                    ),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )

            if (response && response.details) {
                this.setState({
                    apartAssocList: response.details.apartAssocInfo,
                    searchResult: response.details.searchResult,
                })
            }

        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async onClickSearchButton(): Promise<void> {
        this.beforeUserAction()
        // 検索条件を保持
        SessionStorageHelper.setItem(KEY_SEARCH_CONDITION, JSON.stringify(this.state.searchCondition))
        const searchConditionPageInfo: SearchConditionPageInfo = {
            pageNum: this.state.searchConditionPageInfo.pageNum,
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }
        await this.searchApartAssocInfo(
            this.state.searchCondition,
            searchConditionPageInfo,
            this.state.searchConditionSortInfo,
        )
        this.setState({searchConditionPageInfo: searchConditionPageInfo})
    }

    onClickRegisterApartAssocButton(): void {
        this.props.navigate('/admin/apartAssocRegister')
    }

    onChangeInfomationType(event: SelectChangeEvent<unknown>): void {
        const searchCondition: SearchCondition = this.state.searchCondition
        searchCondition.infoCategoryId = event.target.value as string
        searchCondition.categoryId = ''
        this.setState({
            selectedCategory: '',
            selectedInfomationType: event.target.value as string,
            searchCondition: searchCondition,
        })
    }

    onChangeExtractStartDate(date: Nullable<Date>): void {
        const searchCondition: SearchCondition = this.state.searchCondition
        if (date !== null) {
            searchCondition.publishStartDate = formatForDateInput(date.toString())
        } else {
            searchCondition.publishStartDate = ''
        }

        this.setState({
            extractStartDate: date !== null ? date.toString() : '',
            extractEndMinDate: date !== null ? date.toString() : '',
            searchCondition: searchCondition,
        })
    }

    onChangeExtractEndDate(date: Nullable<Date>): void {
        const searchCondition: SearchCondition = this.state.searchCondition
        if (date !== null) {
            searchCondition.publishEndDate = formatForDateInput(date.toString())
        } else {
            searchCondition.publishEndDate = ''
        }

        this.setState({
            extractEndDate: date !== null ? date.toString() : '',
            extractStartMaxDate: date !== null ? date.toString() : '',
            searchCondition: searchCondition,
        })
    }

    onChangeCategory(event: SelectChangeEvent<unknown>): void {
        const searchCondition: SearchCondition = this.state.searchCondition
        searchCondition.categoryId = event.target.value as string
        this.setState({
            selectedCategory: event.target.value as string,
            searchCondition: searchCondition,
        })
    }

    getDisplayInfomationLabel(infoCategoryId: string): string {
        const infomaionLabel =
            INFOMATION_TYPE.codeToEnumConstants(infoCategoryId)
        if (infomaionLabel) {
            return infomaionLabel.label
        } else {
            return ''
        }
    }

    getCategoryLabel(infoCategoryId: string, categoryId: string): string {
        if (infoCategoryId === INFOMATION_TYPE.APART_INFO.value) {
            const categoryLabel = MANSION_CATEGORY.codeToEnumConstants(categoryId)
            if (categoryLabel) {
                return categoryLabel.label
            } else {
                return ''
            }
        } else {
            const categoryLabel = MANAGEMENT_CATEGORY.codeToEnumConstants(categoryId)
            if (categoryLabel) {
                return categoryLabel.label
            } else {
                return ''
            }
        }
    }

    getDisplayVisitorsLabel(visitors: string): string {
        const visitorsLabel =
            VISITORS_RANGE.codeToEnumConstants(visitors)
        if (visitorsLabel) {
            return visitorsLabel.label
        } else {
            return ''
        }
    }

    searchResults(): SearchResultCountDisplayProps {
        if (this.state.apartAssocList.length === 0) {
            if (!Object.keys(this.state.searchCondition).length) {
                return {noResultMessage: Messages.IOPCC0015.toString('マンション・管理組合情報')}
            }
            return {noResultMessage: Messages.IOPCC0014.toString('')}
        }
        return {
            totalCount: this.state.searchResult.total ? this.state.searchResult.total : 0,
            rowsPerPage: this.state.searchConditionPageInfo.pageNum,
            page: this.state.searchConditionPageInfo.page,
        }
    }

    async handleChangePage(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): Promise<void> {
        this.beforeUserAction()
        const searchConditionPageInfo: SearchConditionPageInfo = this.state.searchConditionPageInfo
        searchConditionPageInfo.page = newPage
        await this.searchApartAssocInfo(
            this.state.searchCondition,
            searchConditionPageInfo,
            this.state.searchConditionSortInfo,
        )
        this.setState({searchConditionPageInfo: searchConditionPageInfo})
    }

    async handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.beforeUserAction()
        const searchConditionPageInfo: SearchConditionPageInfo = this.state.searchConditionPageInfo
        searchConditionPageInfo.pageNum = Number(event.target.value)
        searchConditionPageInfo.page = COMMON_CONSTANTS.FIRST_PAGE
        await this.searchApartAssocInfo(
            this.state.searchCondition,
            searchConditionPageInfo,
            this.state.searchConditionSortInfo,
        )
        this.setState({searchConditionPageInfo: searchConditionPageInfo})
    }

    async handleSortLifeNotes(sortKey: string): Promise<void> {
        this.beforeUserAction()

        let sortDirection: SortDirection = this.getSortDirection(sortKey)
        if (sortDirection === false || sortDirection === 'desc') {
            sortDirection = 'asc'
        } else {
            sortDirection = 'desc'
        }

        const sortOrders: SortOrder[] = [new SortOrder(sortKey, sortDirection)]
        await this.searchApartAssocInfo(
            this.state.searchCondition,
            this.state.searchConditionPageInfo,
            sortOrders,
        )
        this.setState({searchConditionSortInfo: sortOrders})
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    getSortDirection(sortKey: string): SortDirection {
        const sortOrder: SortOrder | undefined =
            this.state.searchConditionSortInfo.find(order => order.sortKey === sortKey)
        if (sortOrder === undefined) {
            return false
        }
        return sortOrder.sortDirection
    }

    getFormatForDateInputAllowNull(publishEndDate: string): string {
        if (publishEndDate !== null) {
            return formatForDateInput(publishEndDate)
        } else {
            return publishEndDate
        }
    }

    renderApartAssocInfoTable(apartAssocList: ApartAssocInfo[]): Nullable<ReactElement> {
        if (apartAssocList.length === 0) {
            return null
        }
        const headers: SortableTableHeaderCell[] = [
            {id: 'title', label: 'タイトル'},
            {id: 'dispType', label: '情報種別'},
            {id: 'category', label: 'カテゴリ'},
            {
                id: 'sortOrder', label: '並び順',
                sortable: true, direction: this.getSortDirection('sortOrder'),
            },
            {id: 'publishStartDate', label: '掲載開始'},
            {id: 'publishEndDate', label: '掲載終了'},
            {id: 'visitors', label: '閲覧者'},
            {
                id: 'updateDatetime', label: '更新日時',
                sortable: true, direction: this.getSortDirection('updateDatetime'),
            },
        ]

        return (
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.searchResult.total ? this.state.searchResult.total : 0,
                    rowsPerPage:
                        this.state.searchConditionPageInfo.pageNum ? this.state.searchConditionPageInfo.pageNum : 0,
                    page: this.state.searchConditionPageInfo.page ? this.state.searchConditionPageInfo.page : 0,
                    onPageChange: this.handleChangePage,
                    onRowsPerPageChange: this.handleChangeRowsPerPage,
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                    onSortOrderChange={this.handleSortLifeNotes}
                />
                <SortablePaginationTableBody>
                    {this.state.apartAssocList.map(
                        apartAssoc => this.renderApartAssocListRecord(apartAssoc),
                    )}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        )
    }

    renderApartAssocListRecord(apartAssoc: ApartAssocInfo): ReactElement {
        const apartAssocUrl: string = `/admin/apartAssoc/${apartAssoc.apartAssocInfoId}`
        return (
            <TableRow key={apartAssoc.apartAssocInfoId}>
                <TableCellWordBreakAll align={'center'}>
                    <Link href={apartAssocUrl} underline="hover">{apartAssoc.title}</Link></TableCellWordBreakAll>
                <TableCell align={'center'}>{this.getDisplayInfomationLabel(apartAssoc.infoCategoryId)}</TableCell>
                <TableCell align={'center'}>
                    {this.getCategoryLabel(apartAssoc.infoCategoryId, apartAssoc.categoryId)}</TableCell>
                <TableCell align={'right'}>{apartAssoc.sortOrder}</TableCell>
                <TableCell align={'center'}>{formatForDateInput(apartAssoc.publishStartDate)}</TableCell>
                <TableCell align={'center'}>{this.getFormatForDateInputAllowNull(apartAssoc.publishEndDate)}</TableCell>
                <TableCell align={'center'}>{this.getDisplayVisitorsLabel(apartAssoc.browseAuthority)}</TableCell>
                <TableCell align={'center'}>{formatDateTimeForUpdateDate(apartAssoc.updateDatetime)}</TableCell>
            </TableRow>
        )
    }

    renderInfomationTypeSelect(): ReactNode {
        let infomationTypeList: OptionProps[]
        infomationTypeList = INFOMATION_TYPE.values().map((infomationType) => {
            return ({
                label: `${infomationType.label}`,
                value: `${infomationType.value}`,
            })
        })
        const categoryOptions = [{
            label: '全ての情報',
            value: '',
        }, ...infomationTypeList]

        return (
            <PPSelect
                autoFocus
                eventEmitter={this.props.eventEmitter}
                data={categoryOptions}
                onChange={this.onChangeInfomationType}
                value={this.state.selectedInfomationType}
            />
        )
    }

    renderCategorySelect(): ReactNode {
        let categoryTypeList: OptionProps[] = []
        if (this.state.selectedInfomationType === INFOMATION_TYPE.APART_INFO.value) {
            categoryTypeList = MANSION_CATEGORY.values().map((mansion) => {
                return ({
                    label: `${mansion.label}`,
                    value: `${mansion.value}`,
                })
            })
        } else if (this.state.selectedInfomationType === INFOMATION_TYPE.ASSOC_INFO.value) {
            categoryTypeList = MANAGEMENT_CATEGORY.values().map((management) => {
                return ({
                    label: `${management.label}`,
                    value: `${management.value}`,
                })
            })
        }

        const categoryOptions = [{
            label: '全てのカテゴリ',
            value: '',
        }, ...categoryTypeList]

        return (
            <PPSelect
                eventEmitter={this.props.eventEmitter}
                data={categoryOptions}
                onChange={this.onChangeCategory}
                value={this.state.selectedCategory}
            />
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>
                <ListSearchFormArea
                    searchResult={this.searchResults()}
                    onEnterKey={this.onClickSearchButton}
                >
                    <ConditionTable>
                        <ConditionTableBody>
                            <>
                                <ConditionTableRow>
                                    <ConditionCategoryCell rowSpan={3}>マンション・管理組合情報</ConditionCategoryCell>
                                    {(this.roleId !== ROLE_FOR_ADMINISTRATOR.ASSOC.value) ?
                                        <>
                                            <ConditionColumNameCell>情報種別を選択</ConditionColumNameCell>
                                            <ConditionInputCell>
                                                {this.renderInfomationTypeSelect()}
                                            </ConditionInputCell>
                                        </>
                                        : <ConditionButtonCell></ConditionButtonCell>
                                    }
                                    <ConditionButtonCell></ConditionButtonCell>
                                    <ConditionButtonCell></ConditionButtonCell>
                                </ConditionTableRow>
                                <ConditionTableRow>
                                    <ConditionColumNameCell>抽出範囲</ConditionColumNameCell>
                                    <ConditionInputCell colSpan={3}>
                                        <LeftRowFlexContainer>
                                            <TermWrapper>
                                                <FlexBasis style={{paddingBottom: 4, paddingTop: 8}}>
                                                    <PPDatePicker
                                                        onChangeDate={this.onChangeExtractStartDate}
                                                        value={(this.state.extractStartDate !== '') ?
                                                            new Date(this.state.extractStartDate) : null
                                                        }
                                                        maxDate={(this.state.extractStartMaxDate !== '') ?
                                                            new Date(this.state.extractStartMaxDate) : undefined
                                                        }
                                                        eventEmitter={this.props.eventEmitter}
                                                        eventKeys={['scheduleStartDate']}
                                                    />
                                                </FlexBasis>
                                                <TermTxt>から</TermTxt>
                                            </TermWrapper>
                                            <TermWrapper>
                                                <FlexBasis style={{paddingBottom: 4, paddingTop: 8}}>
                                                    <PPDatePicker
                                                        onChangeDate={this.onChangeExtractEndDate}
                                                        value={(this.state.extractEndDate !== '') ?
                                                            new Date(this.state.extractEndDate) : null
                                                        }
                                                        minDate={(this.state.extractEndMinDate !== '') ?
                                                            new Date(this.state.extractEndMinDate) : undefined
                                                        }

                                                        eventEmitter={this.props.eventEmitter}
                                                        eventKeys={['scheduleStartDate']}
                                                    />
                                                </FlexBasis>
                                                <TermTxt>の間</TermTxt>
                                            </TermWrapper>
                                        </LeftRowFlexContainer>
                                    </ConditionInputCell>
                                </ConditionTableRow>
                                <ConditionTableRow>
                                    <ConditionColumNameCell>カテゴリを選択</ConditionColumNameCell>
                                    <ConditionInputCell colSpan={4}>
                                        {this.renderCategorySelect()}
                                    </ConditionInputCell>
                                    <ConditionButtonCell>
                                        <AdminDefaultMediumButton
                                            onClick={this.onClickSearchButton}>
                                            検索する
                                        </AdminDefaultMediumButton>
                                    </ConditionButtonCell>
                                </ConditionTableRow>
                            </>
                        </ConditionTableBody>
                    </ConditionTable>
                </ListSearchFormArea>
                <SearchPageActionArea>
                    <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_APART_ASSOC_INFO}>
                        <AdminDefaultMediumButton onClick={this.onClickRegisterApartAssocButton}>
                            新規
                        </AdminDefaultMediumButton>
                    </AuthorityChecker>
                </SearchPageActionArea>
                <PageMainArea>
                    {this.renderApartAssocInfoTable(this.state.apartAssocList)}
                </PageMainArea>
            </>
        )
    }
}

export default createAdminPage(NatAdminApartAssocInfoListPage)

const TableCellWordBreakAll = styled(TableCell)`
  &.MuiTableCell-root {
    word-break: break-all;
  }
`

const TermWrapper: StyledDiv = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: flex-end;
`

const TermTxt: StyledSpan = styled.span`
  padding: 0 5px 0 5px;
`

const FlexBasis: StyledDiv = styled.div`
  flex: 1 1 0%;
`
